import dagre from "dagre"
import type { Node, Edge } from "reactflow"
import { DEFAULT_PREFS } from "./types"

export const NODE_HEIGHT = 72
const MARGIN = 60

interface NodeData {
  rank?: number
  [key: string]: unknown
}

export interface LayoutOptions {
  nodeWidth?: number
  rankSpacing?: number
  nodeGap?: number
}

export function applyLayout(
  nodes: Node[],
  edges: Edge[],
  direction: "TB" | "LR" | "manual" | "radial" = "TB",
  opts?: LayoutOptions,
): Node[] {
  if (nodes.length === 0) return nodes

  if (direction === "manual") return applyManualLayout(nodes)
  if (direction === "radial") return applyRadialLayout(nodes, edges, opts)
  return applyDagreLayout(nodes, edges, direction, opts)
}

/** Manual layout — nodes already have positions set in the spec */
function applyManualLayout(nodes: Node[]): Node[] {
  // Process group nodes first, then children
  const groups = nodes.filter((n) => n.data.isGroup)
  const children = nodes.filter((n) => n.parentNode)
  const free = nodes.filter((n) => !n.data.isGroup && !n.parentNode)

  const result: Node[] = []

  // Position groups
  let groupY = 0
  const GROUP_GAP = 40
  groups.forEach((node) => {
    const pos = node.data.specPosition
    const w = node.data.width ?? 300
    const h = node.data.height ?? 200
    result.push({
      ...node,
      position: pos ? { x: pos.x, y: pos.y } : { x: 0, y: groupY },
      style: {
        width: w,
        height: h,
        background: `${node.data.color}08`,
        border: `2px dashed ${node.data.color}30`,
        borderRadius: 14,
        padding: 14,
      },
    })
    groupY += h + GROUP_GAP
  })

  // Position children (use their specPosition as relative to parent)
  children.forEach((node) => {
    const pos = node.data.specPosition
    result.push({
      ...node,
      position: pos ? { x: pos.x, y: pos.y } : node.position,
    })
  })

  // Position free nodes below groups
  free.forEach((node) => {
    const pos = node.data.specPosition
    result.push({
      ...node,
      position: pos ? { x: pos.x, y: pos.y } : { x: 0, y: groupY },
    })
  })

  return result
}

/** Radial layout — center node (rank 0) with rings radiating outward */
function applyRadialLayout(nodes: Node[], edges: Edge[], opts?: LayoutOptions): Node[] {
  const RING_SPACING = opts?.rankSpacing ?? 200
  const NODE_WIDTH = opts?.nodeWidth ?? DEFAULT_PREFS.nodeWidth

  // Group by rank
  const rankGroups = new Map<number, Node[]>()
  nodes.forEach((node) => {
    const rank = (node.data as NodeData).rank ?? 0
    if (!rankGroups.has(rank)) rankGroups.set(rank, [])
    rankGroups.get(rank)!.push(node)
  })

  const sortedRanks = [...rankGroups.keys()].sort((a, b) => a - b)
  const positionMap = new Map<string, { x: number; y: number }>()

  // Center point
  const cx = 400
  const cy = 400

  sortedRanks.forEach((rank, ringIndex) => {
    const group = rankGroups.get(rank)!

    if (ringIndex === 0) {
      // Center ring — stack vertically if multiple
      group.forEach((node, i) => {
        positionMap.set(node.id, {
          x: cx - NODE_WIDTH / 2,
          y: cy - (group.length * NODE_HEIGHT) / 2 + i * (NODE_HEIGHT + 20),
        })
      })
    } else {
      // Outer rings — distribute around circle
      const radius = ringIndex * RING_SPACING
      const angleStep = (2 * Math.PI) / group.length
      const startAngle = -Math.PI / 2 // start at top

      group.forEach((node, i) => {
        const angle = startAngle + i * angleStep
        positionMap.set(node.id, {
          x: cx + radius * Math.cos(angle) - NODE_WIDTH / 2,
          y: cy + radius * Math.sin(angle) - NODE_HEIGHT / 2,
        })
      })
    }
  })

  return nodes.map((node) => ({
    ...node,
    position: positionMap.get(node.id) ?? node.position,
  }))
}

/** Dagre layout — original TB/LR with rank-based positioning */
export function applyDagreLayout(
  nodes: Node[],
  edges: Edge[],
  direction: "TB" | "LR" = "TB",
  opts?: LayoutOptions,
): Node[] {
  if (nodes.length === 0) return nodes

  // Separate group nodes from regular nodes
  const groupNodes = nodes.filter((n) => n.data.isGroup)
  const childNodes = nodes.filter((n) => n.parentNode && !groupNodes.find((g) => g.id === n.id))
  const freeNodes = nodes.filter((n) => !n.data.isGroup && !n.parentNode)

  // If we have group nodes, layout each group's children independently
  if (groupNodes.length > 0) {
    return layoutWithGroups(groupNodes, childNodes, freeNodes, edges, direction, opts)
  }

  return layoutFreeNodes(freeNodes, edges, direction, opts)
}

function layoutWithGroups(
  groupNodes: Node[],
  childNodes: Node[],
  freeNodes: Node[],
  edges: Edge[],
  direction: "TB" | "LR",
  opts?: LayoutOptions,
): Node[] {
  const NODE_WIDTH = opts?.nodeWidth ?? DEFAULT_PREFS.nodeWidth
  const GROUP_PAD = 40
  const GROUP_HEADER = 50
  const GROUP_GAP = 60

  const result: Node[] = []
  let groupOffsetX = 0

  // Layout each group's children with dagre, then position the group
  for (const group of groupNodes) {
    const children = childNodes.filter((n) => n.parentNode === group.id)
    const childIds = new Set(children.map((c) => c.id))
    const groupEdges = edges.filter((e) => childIds.has(e.source) && childIds.has(e.target))

    // Run dagre on children
    const laidChildren = layoutFreeNodes(children, groupEdges, direction, opts)

    // Calculate bounds of children
    let minX = Infinity,
      minY = Infinity,
      maxX = -Infinity,
      maxY = -Infinity
    laidChildren.forEach((n) => {
      minX = Math.min(minX, n.position.x)
      minY = Math.min(minY, n.position.y)
      maxX = Math.max(maxX, n.position.x + NODE_WIDTH)
      maxY = Math.max(maxY, n.position.y + NODE_HEIGHT)
    })

    const childWidth = maxX - minX
    const childHeight = maxY - minY

    // Position group node
    const groupWidth = group.data.width || childWidth + GROUP_PAD * 2
    const groupHeight = group.data.height || childHeight + GROUP_PAD + GROUP_HEADER

    result.push({
      ...group,
      position: { x: groupOffsetX, y: 0 },
      style: {
        width: groupWidth,
        height: groupHeight,
        ...(group.style || {}),
      },
    })

    // Position children relative to group
    laidChildren.forEach((child) => {
      result.push({
        ...child,
        position: {
          x: child.position.x - minX + GROUP_PAD,
          y: child.position.y - minY + GROUP_HEADER,
        },
      })
    })

    groupOffsetX += groupWidth + GROUP_GAP
  }

  // Layout free nodes separately
  if (freeNodes.length > 0) {
    const laid = layoutFreeNodes(freeNodes, edges, direction, opts)
    laid.forEach((n) => {
      result.push({
        ...n,
        position: { x: n.position.x + groupOffsetX, y: n.position.y },
      })
    })
  }

  return result
}

function layoutFreeNodes(nodes: Node[], edges: Edge[], direction: "TB" | "LR", opts?: LayoutOptions): Node[] {
  if (nodes.length === 0) return nodes

  const isVertical = direction === "TB"
  const NODE_WIDTH = opts?.nodeWidth ?? DEFAULT_PREFS.nodeWidth
  const NODE_GAP = opts?.nodeGap ?? DEFAULT_PREFS.nodeGap
  const RANK_SPACING = opts?.rankSpacing ?? (isVertical ? 140 : 200)

  // Step 1: Run dagre for edge-crossing-aware ordering
  const g = new dagre.graphlib.Graph()
  g.setDefaultEdgeLabel(() => ({}))
  g.setGraph({
    rankdir: direction,
    ranksep: RANK_SPACING,
    nodesep: NODE_GAP,
    marginx: MARGIN,
    marginy: MARGIN,
  })

  const nodeIds = new Set(nodes.map((n) => n.id))
  nodes.forEach((node) => {
    g.setNode(node.id, { width: NODE_WIDTH, height: NODE_HEIGHT })
  })

  edges.forEach((edge) => {
    if (nodeIds.has(edge.source) && nodeIds.has(edge.target)) {
      g.setEdge(edge.source, edge.target)
    }
  })

  dagre.layout(g)

  // Step 2: Group nodes by rank
  const rankGroups = new Map<number, { id: string; dagreX: number; dagreY: number }[]>()

  nodes.forEach((node) => {
    const rank = (node.data as NodeData).rank ?? 0
    if (!rankGroups.has(rank)) rankGroups.set(rank, [])
    const pos = g.node(node.id)
    rankGroups.get(rank)!.push({ id: node.id, dagreX: pos.x, dagreY: pos.y })
  })

  const sortedRanks = [...rankGroups.keys()].sort((a, b) => a - b)

  // Step 3: Position — force rank tiers, use dagre ordering within tier
  const positionMap = new Map<string, { x: number; y: number }>()

  sortedRanks.forEach((rank, tierIndex) => {
    const group = rankGroups.get(rank)!

    // Sort by dagre's cross-axis position (preserves edge-crossing optimization)
    group.sort((a, b) => (isVertical ? a.dagreX - b.dagreX : a.dagreY - b.dagreY))

    // Distribute evenly within tier
    const totalSpan = group.length * NODE_WIDTH + (group.length - 1) * NODE_GAP
    const startCross = -totalSpan / 2

    group.forEach((item, i) => {
      const crossPos = startCross + i * (NODE_WIDTH + NODE_GAP)
      const mainPos = MARGIN + tierIndex * (RANK_SPACING + NODE_HEIGHT)

      positionMap.set(item.id, isVertical ? { x: crossPos, y: mainPos } : { x: mainPos, y: crossPos })
    })
  })

  return nodes.map((node) => ({
    ...node,
    position: positionMap.get(node.id) ?? { x: 0, y: 0 },
  }))
}

/** Calculate Y (or X for LR) ranges for each layer band */
export function getLayerBands(
  nodes: Node[],
  direction: "TB" | "LR" | "manual" | "radial",
): { layerId: string; layerName: string; color: string; start: number; end: number }[] {
  const isVertical = direction === "TB" || direction === "manual" || direction === "radial"
  const layerRanges = new Map<string, { layerName: string; color: string; min: number; max: number }>()

  nodes.forEach((node) => {
    const d = node.data as { layer: string; layerName: string; color: string }
    const pos = isVertical ? node.position.y : node.position.x
    const size = isVertical ? NODE_HEIGHT : ((node.data as { width?: number }).width ?? DEFAULT_PREFS.nodeWidth)

    if (!layerRanges.has(d.layer)) {
      layerRanges.set(d.layer, { layerName: d.layerName, color: d.color, min: pos, max: pos + size })
    } else {
      const r = layerRanges.get(d.layer)!
      r.min = Math.min(r.min, pos)
      r.max = Math.max(r.max, pos + size)
    }
  })

  const PAD = 20
  return [...layerRanges.entries()].map(([layerId, r]) => ({
    layerId,
    layerName: r.layerName,
    color: r.color,
    start: r.min - PAD,
    end: r.max + PAD,
  }))
}
